import { useContext } from 'react';
import CONSTANTS from "./constants";
import { EntityDraftViewModel } from '../interfaces/EntityDraftViewModel';
import { getUserJourneyTasks } from '../services/bff-service';
import { fetchEntityDraft } from '../stores/async/async-actions';
import { capitalizeFirstLetter, toUrlSlug } from "./common-utils";
import { setInitialJourneyTasks, setSelectedEntity, setSelectedJourneyTask, setJourneyTasksLoadInProgress } from '../stores/entity/entity-action';
import FENERGO_CONSTANTS from "./fenergo-constants";
import STATIC_TEXT from "../content/common.json";
import URL from './urls';
import ROUTES from './routes';
import { setCurrentStepperIndex } from "../stores/ui/ui-action";
import { JourneyInfo } from "../interfaces/JourneyInfo.interface";
import { JourneyTaskAttributes } from "../interfaces/JourneyTaskAttributes.interface";
import moment from "moment";

export const getFenXUrl = (relativeUrl: string) => {
    return URL.fenergo.basePath + encodeURIComponent(relativeUrl);
}

export const filterJourneyTasks: any = (jTasks: any) => {
    return jTasks.filter(jTask => {
        return (
            jTask.taskType === 'PolicyTask'
            || jTask.taskType === 'RelatedPartyJourneyCheckpointTask'
            || jTask.taskType === 'CombinedPolicyTask'
            //|| (jTask.taskType === 'ManualTask' && jTask.status === 'In Progress')
            //|| (jTask.taskType === 'ManualTask')
        )
    });
}

export const getMenuOptions = (journeyTasks, journeyId, taskId, labelSuffixForSelectedOption, taskName = null) => {
    //console.log("JourneyInfo",journeyInfo);
    return journeyTasks
        ? journeyTasks.map(journeyTask => {
            const newOption = (toUrlSlug(journeyTask.name) === taskName);
            const oldOption = (journeyTask.id === taskId);
            const isSelectedOption = taskName ? newOption : oldOption;
            return {
                link: `/journey/${journeyId}/task/${journeyTask.id}`,
                label: journeyTask.name,
                title: journeyTask.name + (isSelectedOption && labelSuffixForSelectedOption ? ` / ${labelSuffixForSelectedOption}` : ''),
                selected: isSelectedOption
            }
        })
        : [];
}

export const fetchJourneyTasks = async (
    dispatch: React.Dispatch<any>,
    history: any,
    selectedEntity: any,
    journeyId: string,
    setJourneyTasks: Function,
    clearJourneyTasksLoadInProgress: boolean,
    readableUrls: boolean,
    taskId?: string
) => {
    let journeyTasks = await getUserJourneyTasks(journeyId);
    journeyTasks = filterJourneyTasks(journeyTasks);
    dispatch(setJourneyTasks(journeyTasks))
    const inProgressJourneyTask = taskId ? journeyTasks.find(task => task.id === taskId) : journeyTasks.find(task => task.status === CONSTANTS.TASK_IN_PROGRESS || task.status === 'Error');
    const isTaskNotStarted = journeyTasks.every(task => task.status === CONSTANTS.TASK_NOT_STARTED)
    const selectedJourneyTask = inProgressJourneyTask ? inProgressJourneyTask : isTaskNotStarted ? journeyTasks[0] : '';
    //'Temporary change so that the users can revisit completed journeys until we create the application trigger to more easily start new journeys'
    const tempSelectedJourneyTask = inProgressJourneyTask ? inProgressJourneyTask : journeyTasks[0];
    const tempSelectedJourneyTaskIndex = journeyTasks.findIndex(task => task.id === tempSelectedJourneyTask.id);
    dispatch(setSelectedJourneyTask(tempSelectedJourneyTask));
    dispatch(setCurrentStepperIndex(tempSelectedJourneyTaskIndex));
    clearJourneyTasksLoadInProgress && dispatch(setJourneyTasksLoadInProgress(false));
    //Passing selectedJourneyTask instead tempSelectedJourneyTask
    redirectToUserNextSteps(history, selectedEntity, journeyTasks, selectedJourneyTask, readableUrls);
}

export const fetchEntityAndJourneyData = async (
    dispatch: React.Dispatch<any>,
    history: any,
    entity: any,
    journeyTasksLoadInProgress: boolean,
    journeyId: any,
    readableUrls: boolean,
    taskId?: string
) => {
    if (journeyTasksLoadInProgress) return;
    dispatch(setJourneyTasksLoadInProgress(true));

    const selectedEntity = fetchEntityInfo(entity);
    const currentJourneyId = journeyId ? journeyId : fetchJourneyId(entity);

    dispatch(setSelectedEntity(selectedEntity));
    dispatch(fetchEntityDraft(selectedEntity.id, currentJourneyId));

    // Fetch Journey Tasks
    await fetchJourneyTasks(dispatch, history, selectedEntity, currentJourneyId, setInitialJourneyTasks, true, readableUrls, taskId);

    // Can be enabled if there any conditional tasks added to the journey
    // setInterval(() => {
    //     fetchJourneyTasks(dispatch, history, selectedEntity, currentJourneyId, setLatestJourneyTasks);
    // }, 500000);
}

// Extract company entity or individual entity based on journeyId
export const fetchEntityInfo = (entity: any) => {
    return (!entity.journeyInfo.journeyId
        || (entity.company.journeys.find(journey => journey.journeyId === entity.journeyInfo.journeyId)))
        ? entity.company
        : entity.individual
}

export const fetchJourneyInfo = (entity: any) => {
    const entityInfo = fetchEntityInfo(entity);
    return getInProgressOrFirstJourney(entityInfo.journeys);
}

// TODO: Remove temp journeyId filter
export const fetchJourneyId = (entity: any) => {
    if (entity.journeyInfo.journeyId) {
        return entity.journeyInfo.journeyId;
    }
    return getInProgressOrFirstJourney(entity.company.journeys)?.journeyId;
}

export const getInProgressOrFirstJourney = (journeys: JourneyInfo[]) => {
    return journeys.find(journey => journey.journeyStatus === CONSTANTS.JOURNEY_IN_PROGRESS) ?? journeys[0];
}

export const fetchUserNextSteps = (selectedEntity, initialJourneyTasks, inProgressJourneyTask, readableUrls = false) => {
    const journeyInfo = getInProgressOrFirstJourney(selectedEntity.journeys);
    //console.log("JourneyInfo",journeyInfo);
    /**
    * Added condition to check the status of journey is done and any next task
    * is in progress or not, then redirect to thank you page 
    */
    if (journeyInfo.journeyStatus === FENERGO_CONSTANTS.JOURNEY_STATUSES.DONE || inProgressJourneyTask === '') {
        return ROUTES.thankYou;
    } else if (inProgressJourneyTask) {
        return readableUrls ? ROUTES.genericTaskDetailsByName(journeyInfo.journeyType, inProgressJourneyTask.name) : ROUTES.genericTaskDetails(journeyInfo.journeyId, inProgressJourneyTask.id);
    } else {
        return getNextWelcomeUrl(selectedEntity, 0);
    }
}

export const redirectToUserNextSteps = (navigate, selectedEntity, initialJourneyTasks, inProgressJourneyTask, readableUrls) => {
    if (navigate) navigate(fetchUserNextSteps(selectedEntity, initialJourneyTasks, inProgressJourneyTask, readableUrls));
}

export const isCompany = (peopleEntity) => peopleEntity.type === CONSTANTS.COMPANY;

export const isIndividual = (peopleEntity) => peopleEntity.type === CONSTANTS.INDIVIDUAL;

export const isIndividualRelatedParty = (relatedPartyType: string) => relatedPartyType === CONSTANTS.INDIVIDUAL;

export const getParentBranch = (entityDraft: any) => {
    return entityDraft.properties.fromWhichSvbBranchWillTheProspectRequireBankingServices.value
}

export const getTargetId = (entities, journeyId) => {
    const matchedEntity = entities.find(entity => entity.journeyInfo.journeyId === journeyId);
    return matchedEntity ? matchedEntity.company.id : null;
}

export const isInvitedIndividual = (formData, entityDraft) => {
    // For UK check - This is temporary fix untill FenX configure GCO-9873
    // FenX will calculate when to show/hide
    return (isUKEntity(entityDraft) && formData.relationship !== FENERGO_CONSTANTS.APPLICANT_ROLE)
        || (isUSEntity(entityDraft) && formData[FENERGO_CONSTANTS.ADD_RELATED_INDIVIDUAL.SEND_THE_LINK_FIELD] === FENERGO_CONSTANTS.ADD_RELATED_INDIVIDUAL.SEND_THE_LINK_VALUE)
}

export const relatedPartyName = (formData) => {
    return [capitalizeFirstLetter(formData.firstName), capitalizeFirstLetter(formData.lastName)].join(' ');
}

export const getRelatedPartyIDVCategories = (entityDraft: EntityDraftViewModel) => {
    if (isUSEntity(entityDraft)) {
        return FENERGO_CONSTANTS.RELATED_PARTY_US_IDV_CATEGORIES;
    } else if (isUKEntity(entityDraft)) {
        return FENERGO_CONSTANTS.RELATED_PARTY_UK_IDV_CATEGORIES;
    }

    return [];
}

/**
 * Function checks whether entity draft contains US jurisdiction
 * @param entityDraft 
 * @returns true/false
 */
export const isUSEntity = (entityDraft: any) => {
    const { jurisdictions } = entityDraft;

    return jurisdictions
        && jurisdictions.some((jObj: any) => jObj.jurisdiction === CONSTANTS.UNITED_STATES);
}

/**
 * Function checks whether entity draft contains UK jurisdiction
 * @param entityDraft 
 * @returns true/false
 */
export const isUKEntity = (entityDraft: any) => {
    const { jurisdictions } = entityDraft;

    return jurisdictions
        && jurisdictions.some((jObj: any) => jObj.jurisdiction === CONSTANTS.UNITED_KINGDOM);
}

/**
 * Function checks whether entity draft contains US as countries of citizenship
 * @param entityDraft 
 * @returns true/false
 */
export const isUSCitizen = (entityDraft: EntityDraftViewModel) => {
    const countriesOfCitizenship = entityDraft.properties[FENERGO_CONSTANTS.POLICY_FEILDS.COUNTRIES_OF_CITIZENSHIP]?.value || '';
    // Check if countriesOfCitizenship contains "United States"
    return countriesOfCitizenship.includes(CONSTANTS.UNITED_STATES);
}

/**
 * Function checks whether individual's country of residence is UK or not
 * @param entityDraft 
 * @returns true/false
 */
export const isUKResident = (entityDraft: any) => {
    const { properties: { residentialAddress } } = entityDraft;

    if (!residentialAddress) return false;

    let residentialCountries: string[] = [];
    if (residentialAddress.collections) {
        Object.values(residentialAddress.collections).forEach((address: any) => {
            if (address?.properties.RPcountry) residentialCountries.push(address.properties.RPcountry)
        });
    } else if (residentialAddress.properties) {
        residentialCountries.push(residentialAddress.properties.RPcountry.value)
    }

    return residentialCountries.some((country: string) => country === CONSTANTS.UNITED_KINGDOM)
}

/**
 * Function that checks the entity draft for overallIdvStatus
 * @param entityDraft entity draft
 * @returns idv status 
 */
export const isIdvStatusVerified = (entityDraft: any) => {
    const idvStatus = entityDraft?.properties.overallIdvStatus?.value || '';
    return idvStatus.toLowerCase() === CONSTANTS.TRUE.toLowerCase();
}

/**
 * Function that checks all the Entity IDV fields and then returns the status
 * If all the Entity IDV fields are true, then return true
 * For any other case, return false
 * @param entityDraftData entity draft data
 * @returns returns if Entity IDV fields are all verified
 */
export const getEntityIDVStatus = (entityDraftData) => {
    // Get the field keys
    let idvFields;
    switch (entityDraftData.properties.countryOfIncorporationEstablishmentRegistration?.value) {
        case FENERGO_CONSTANTS.ENTITY_COUNTRY_FIELD.COUNTRY_NAME.UNITED_STATES:
            idvFields = FENERGO_CONSTANTS.ENTITY_IDV_FIELDS;
            break;
        default:
            idvFields = FENERGO_CONSTANTS.NON_US_ENTITY_IDV_FIELDS;
            break;
    }

    // Get their response values
    const idvStatusResponses = idvFields.map(field => entityDraftData.properties[field]?.value);

    // Undefined values are considered as false 
    return idvStatusResponses.every(element => element === CONSTANTS.TRUE);
}

/**
 * 
 * @param entityDraft entity draft
 * @param idvField idvField to be checked, 
 *  e.g. FENERGO_CONSTANTS.COMMON_IDV_FIELDS.NAME_VERIFIED
 */
export const getFormattedReponseValue = (entityDraft: EntityDraftViewModel, idvField: any) => {
    if (!entityDraft || !entityDraft.properties) return '';

    const responseFields = idvField.RESPONSE_FIELDS;
    const formatter = idvField.FORMATTER;

    // Format the response values
    switch (formatter) {
        case FENERGO_CONSTANTS.FORMATTED_RESPONSE_FIELD.ADDRESS:
            // create a address format string
            return formatAddress(entityDraft.properties.residentialAddress);
        case FENERGO_CONSTANTS.FORMATTED_RESPONSE_FIELD.GOVERNMENT_ID:
            // Get the documentNumber value from the properties of governmentId
            return formatGovernmentIdNumber(entityDraft);
        case FENERGO_CONSTANTS.FORMATTED_RESPONSE_FIELD.COMPANY_NAME:
            // Get company Name for registration verification result
            return entityDraft.properties.companyLegalName?.value;
        case FENERGO_CONSTANTS.FORMATTED_RESPONSE_FIELD.EIN:
            // Get company registered number for registration verification result
            return entityDraft.properties.employerIdentificationNumberEin?.value;
        case FENERGO_CONSTANTS.FORMATTED_RESPONSE_FIELD.EIN_PENDING:
            // Get pending ein  for registration verification result
            return CONSTANTS.PENDING_EIN;
        case FENERGO_CONSTANTS.FORMATTED_RESPONSE_FIELD.REGISTRATION_NUMBER:
            // Get company registered number for registration verification result
            return entityDraft.properties.registrationNumber?.value;
        case FENERGO_CONSTANTS.FORMATTED_RESPONSE_FIELD.COMPANY_PRIMARY_ADDRESS:
            // Get company physical for registration verification result
            // return responseFields.map(f => entityDraft.properties.primaryPhysicalAddress?.properties[f]?.value).join(' ');
            return responseFields.map(f => entityDraft.properties[f]?.value).join(' ');
        case FENERGO_CONSTANTS.FORMATTED_RESPONSE_FIELD.NAME:
        case FENERGO_CONSTANTS.FORMATTED_RESPONSE_FIELD.DATE:

        default:
            // Space separated values
            return responseFields.map(f => entityDraft.properties[f]?.value).join(' ');
    }
}



/**
 * Function that returns idv fields that need to be checked. 
 * US and Non-US entities have different idv fields.
 * @param entityDraft entity draft
 * @returns idvFields to be checked
 */
export const getIdvFields = (entityDraft: EntityDraftViewModel) => {
    // Check if entityDraft is present, if not return empty array
    if (!entityDraft) return [];

    if (isUKEntity(entityDraft)) {
        if (isUSCitizen(entityDraft) && !isUKResident(entityDraft)) {
            // Show SSN only for Non UK Resident and US citizens
            return { ...FENERGO_CONSTANTS.COMMON_IDV_FIELDS, ...FENERGO_CONSTANTS.US_INDIVIDUAL_IDV };
        }

        if (isUKResident(entityDraft)) {
            // Show common fields for US citizens or UK residents
            return FENERGO_CONSTANTS.COMMON_IDV_FIELDS;
        }

        // Non UK Resident and Non US citizens
        return { ...FENERGO_CONSTANTS.COMMON_IDV_FIELDS, ...FENERGO_CONSTANTS.NON_US_INDIVIDUAL_IDV }
    }

    const idvProps = isUSCitizen(entityDraft)
        ? { ...FENERGO_CONSTANTS.COMMON_IDV_FIELDS, ...FENERGO_CONSTANTS.US_INDIVIDUAL_IDV }
        : { ...FENERGO_CONSTANTS.COMMON_IDV_FIELDS, ...FENERGO_CONSTANTS.NON_US_INDIVIDUAL_IDV };
    return idvProps;
}

/**
 * Function that returns idv fields that need to be checked.
 * e.g. legalNameIDStatus
 * @param entityDraft entity draft
 * @returns idv status fields
 */
export const getIdvKeys = (entityDraft: EntityDraftViewModel) => {
    const idvFields = getIdvFields(entityDraft);
    return Object.keys(idvFields).map(key => idvFields[key].VERIFY_FIELD);
}

/**
 * Function that returns a formatted address 
 * e.g. '123 Main St, San Jose, CA95111'
 * @param address address to be formatted
 * @returns 
 */
export const formatAddress = (address: any) => {
    if (!address || (!address.properties && !address.collections)) return '';

    const addressProps = address.properties || (Object.values(address.collections)[0] as any)?.properties;

    if (!addressProps) return '';

    // Create State and Zip combined as a single string
    if (address.properties) {
        let stateAndZip = [
            ...FENERGO_CONSTANTS.RESIDENTIAL_ADDRESS.STATE.map(field => addressProps[field]?.value),
            ...FENERGO_CONSTANTS.RESIDENTIAL_ADDRESS.ZIP.map(field => addressProps[field]?.value)
        ].filter(Boolean).join(' ');

        // Format Address Line 1 and City and StateAndZip, separated by comma
        const formattedAddress = [
            addressProps[FENERGO_CONSTANTS.RESIDENTIAL_ADDRESS.ADDRESS_LINE_1]?.value,
            addressProps[FENERGO_CONSTANTS.RESIDENTIAL_ADDRESS.CITY]?.value,
            stateAndZip
        ].filter(Boolean).join(', ');

        return formattedAddress;
    } else if (address.collections) {
        let stateAndZip = [
            ...FENERGO_CONSTANTS.RESIDENTIAL_ADDRESS.STATE.map(field => addressProps[field]),
            ...FENERGO_CONSTANTS.RESIDENTIAL_ADDRESS.ZIP.map(field => addressProps[field])
        ].filter(Boolean).join(' ');

        const formattedAddress = [
            addressProps[FENERGO_CONSTANTS.RESIDENTIAL_ADDRESS.ADDRESS_LINE_1],
            addressProps[FENERGO_CONSTANTS.RESIDENTIAL_ADDRESS.CITY],
            stateAndZip
        ].filter(Boolean).join(', ');

        return formattedAddress;
    }
}

/**
 * Function that extracts the government id number from data groups
 */
export const formatGovernmentIdNumber = (entityDraft) => {
    const { properties: { governmentId } } = entityDraft;
    if (!governmentId) {
        return "";
    } else if (governmentId.properties) {
        return governmentId.properties.documentNumber?.value
    } else if (governmentId.collections && Object.values(governmentId.collections).length > 0) {
        return (Object.values(governmentId.collections)[0] as any).properties.documentNumber
    } else {
        return "";
    }
}

/**
 * Function that checks all the IDV fields and then returns the status
 * If all the IDV fields are true, then return true
 * For any other case, return false
 * @param entityDraftData entity draft data
 * @returns returns if IDV fields are all verified
 */
export const getIDVStatus = (entityDraftData) => {
    // Get the field keys
    const idvFields = getIdvKeys(entityDraftData);

    // Get their response values
    const idvStatusResponses = idvFields.map(field => entityDraftData.properties[field]?.value);

    // Undefined values are considered as false 
    return idvStatusResponses.every(element => element === CONSTANTS.TRUE);
}

export const isIndividualVerified = (partyType, partyProps) => {
    return (partyType === CONSTANTS.INDIVIDUAL) && (partyProps?.overallIdvStatus === CONSTANTS.TRUE)
}

export const fetchInvitedIndividualStatus = (individual, entityDraft, allEntityDraftList) => {
    if (allEntityDraftList && allEntityDraftList.length === 1) {
        if (allEntityDraftList[0].overallIdvStatus === CONSTANTS.TRUE) return STATIC_TEXT.verifyIdentity.identityVerified;
        else if (allEntityDraftList[0].invitedIndividual === FENERGO_CONSTANTS.ADD_RELATED_INDIVIDUAL.SEND_THE_LINK_VALUE) return STATIC_TEXT.relatedParty.inviteToBeSent;
        else return null;
    } else {
        const getEntityDraft = allEntityDraftList && allEntityDraftList.find(draft => draft.invitedIndividual === FENERGO_CONSTANTS.ADD_RELATED_INDIVIDUAL.SEND_THE_LINK_VALUE && draft.sourceEntityId === individual.sourceEntityId && draft.journeyId !== entityDraft.journeyId)
        if (getEntityDraft && getEntityDraft.overallIdvStatus === CONSTANTS.TRUE) return STATIC_TEXT.verifyIdentity.identityVerified;
        else if (getEntityDraft) return STATIC_TEXT.relatedParty.inviteSent;
        else if (individual.invitedIndividual === FENERGO_CONSTANTS.ADD_RELATED_INDIVIDUAL.FILL_OUT_FOR_THEM || !individual.invitedIndividual || individual.invitedIndividual === FENERGO_CONSTANTS.ADD_RELATED_INDIVIDUAL.SEND_THE_LINK_VALUE) {
            if (individual.overallIdvStatus === CONSTANTS.TRUE) return STATIC_TEXT.verifyIdentity.identityVerified;
            else return individual.invitedIndividual === FENERGO_CONSTANTS.ADD_RELATED_INDIVIDUAL.FILL_OUT_FOR_THEM ? null : STATIC_TEXT.relatedParty.inviteToBeSent;
        }
    }
}

/**
 * Function that returns registration verification result fields that need to be checked. 
 * @param entityDraft entity draft
 * @returns registrationProps to be checked
 */
export const getRegistraionVerificationField = (entityDraft: EntityDraftViewModel) => {
    // Check if entityDraft is present, if not return empty array
    if (!entityDraft) return [];
    let verificationFieldProps: any = {};
    switch (entityDraft.properties?.country?.value) {
        case FENERGO_CONSTANTS.ENTITY_COUNTRY_FIELD.COUNTRY_NAME.UNITED_STATES:
            verificationFieldProps = { ...FENERGO_CONSTANTS.REGISTRATION_VERIFICATION_FILEDS, ...FENERGO_CONSTANTS.US_REGISTRATION_ADDRESS_FIELD };
            break;
        case FENERGO_CONSTANTS.ENTITY_COUNTRY_FIELD.COUNTRY_NAME.UNITED_KINGDOM:
            verificationFieldProps = { ...FENERGO_CONSTANTS.REGISTRATION_VERIFICATION_FILEDS, ...FENERGO_CONSTANTS.UK_REGISTRATION_ADDRESS_FIELD };
            break;
        case FENERGO_CONSTANTS.ENTITY_COUNTRY_FIELD.COUNTRY_NAME.CANADA:
            verificationFieldProps = { ...FENERGO_CONSTANTS.REGISTRATION_VERIFICATION_FILEDS, ...FENERGO_CONSTANTS.CANADA_REGISTRATION_ADDRESS_FIELD };
            break;
        default:
            verificationFieldProps = { ...FENERGO_CONSTANTS.REGISTRATION_VERIFICATION_FILEDS, ...FENERGO_CONSTANTS.OTHER_COUNTRY_REGISTRATION_ADDRESS_FIELD };
            break;
    }
    if (entityDraft.properties?.countryOfIncorporationEstablishmentRegistration?.value === FENERGO_CONSTANTS.ENTITY_COUNTRY_FIELD.COUNTRY_NAME.UNITED_STATES) {
        if (entityDraft.properties?.myEinIsPending?.value === FENERGO_CONSTANTS.US_EIN_REGISTRATION_PENDING_FIELD.EIN_VERIFIED.PENDING_VALUE) {
            return { ...verificationFieldProps, ...FENERGO_CONSTANTS.US_EIN_REGISTRATION_PENDING_FIELD };
        }
        return { ...verificationFieldProps, ...FENERGO_CONSTANTS.US_EIN_REGISTRATION_FIELD };
    } else {
        return { ...verificationFieldProps, ...FENERGO_CONSTANTS.NON_US_EIN_REGISTRATION_FIELD }
    }
}

export const trimNumbering = (field) => {
    return field && field.replace(/[0-9]+|\.+/gmi, '')
}

export const getJourneyType = (entity) => {
    return getInProgressOrFirstJourney(entity.journeys).journeyType;
}
export const getJourneyId = (entity) => {
    return getInProgressOrFirstJourney(entity.journeys).journeyId;
}

export const getJourneyIdFromDraft = (entityDraft) => {
    return entityDraft.journeyId;
}

export const getNextWelcomeUrl = (entity, urlIndex) => {
    return ROUTES.WELCOME[getJourneyType(entity)][urlIndex]?.url(getJourneyId(entity));
}

export const getCurrentIndex = (entity, currentPath) => {
    return ROUTES.WELCOME[getJourneyType(entity)]
        .findIndex(nextPath => nextPath.url(getJourneyId(entity))
            .indexOf(currentPath) !== -1)
}

export const filterFields = (field, entityType, fieldsToRender) => {
    return field.entityType === entityType &&
        ((field.dataField && fieldsToRender.indexOf(field.dataField.propertyName) >= 0)
            || (field.documentDataKey && fieldsToRender.indexOf(field.documentDataKey) >= 0))
}

/**
 * Function that returns true/false for skipping verify identity
 * @param entityDraft entity draft
 */
export const skipVerifyIdentityCheck = (entityDraft: any) => {
    // If UK jurisdiction and NOT a United States citizen  and NOT a United Kingdom resident, skip 
    if (isUKEntity(entityDraft) && !isUSCitizen(entityDraft) && !isUKResident(entityDraft)) {
        return true;
    }
    return false;
}

export const getTaskFromSlug = (tasks: JourneyTaskAttributes[], slugPathSegment) => {
    return tasks.find(task => slugPathSegment === toUrlSlug(task.name));
}

export const getTaskFromId = (tasks: JourneyTaskAttributes[], taskId) => {
    return tasks.find(task => task.id === taskId);
}

export const getJourneyFromSlug = (journeys: JourneyInfo[], slugPathSegment) => {
    return journeys.find(journey => slugPathSegment === toUrlSlug(journey.journeyType) && journey.journeyStatus === FENERGO_CONSTANTS.JOURNEY_STATUSES.IN_PROGRESS);
}

export const getUniqueJurisdictions = (
    dynamicJurisdictions,
    entityDraftJurisdictions
) => {
    const mappedDynamicJurisdictions = dynamicJurisdictions.map((item) => ({
        jurisdiction: item.jurisdiction,
        versionId: item.requirementSetId,
    }));

    const globalJurisdiction = entityDraftJurisdictions.filter((item) => item.jurisdiction === FENERGO_CONSTANTS.GLOBAL_JUDISTRICTION);
    const combinedJurisdictions = [
        ...globalJurisdiction,
        ...mappedDynamicJurisdictions,
    ];

    const uniqueData = {};
    combinedJurisdictions.forEach((item) => {
        if (!uniqueData.hasOwnProperty(item.jurisdiction)) {
            uniqueData[item.jurisdiction] = item;
        }
    });

    return Object.values(uniqueData);
};

export const getValidJourney = (journeys:JourneyInfo[])=>{
    return getInprogressJourney(journeys) || getLatestCompletedJourney(journeys);
};

export const getInprogressJourney= (journeys:JourneyInfo[])=>{
    const inprogressJourney = journeys?.find((journey)=>journey?.journeyStatus===FENERGO_CONSTANTS.JOURNEY_STATUSES.IN_PROGRESS);
    return inprogressJourney || null;
};

export const getLatestCompletedJourney= (journeys:JourneyInfo[])=>{
    const completedJourney = journeys?.filter(journey=>journey?.journeyStatus===FENERGO_CONSTANTS.JOURNEY_STATUSES.DONE).reduce((prev,curr)=>{
        if(prev){
            const isLatest = moment(prev?.end_datetime).isAfter(curr?.end_datetime)
            return isLatest ? prev : curr;
        }else{
            return curr;
        }
    },null);
    return completedJourney || null;
};

export function transformMultipleJourneysIntoSingle(multipleJourneys: any): any {
    const individual = multipleJourneys.individual
    const companies = multipleJourneys.companies

    if (companies.length === 0) {
        return []
    }

    const newestCompany = companies.reduce((latest, company) => {
        return new Date(company.created) > new Date(latest.created) ? company : latest;
    })

    const firstInProgressJourney = newestCompany.journeys.find(journey => journey.journeyStatus === "In Progress")

    const orignalSingleJourneyAndEntitiesShape = {
        individual: individual,
        company: newestCompany,
        journeyInfo: firstInProgressJourney ? { journeyId: firstInProgressJourney.journeyId } : {}
    }

    return [orignalSingleJourneyAndEntitiesShape];
}

export function hasInProgressJourney(companies: any[]): boolean {
    if (!Array.isArray(companies) || companies.length === 0) return false;
    
    return companies.some(company =>
        company.journeys.some(journey => journey.journeyStatus === 'In Progress')
    );
}